import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, CalendarClock, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getNightAuditAdminConfigs, updateNightAuditAdminConfig } from '../utils/api';

const DAYS_OF_WEEK = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
];

const FREQUENCY_LABELS = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };

const selectClass = `
    text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1
    bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100
    focus:outline-none focus:ring-2 focus:ring-orange-400
    disabled:opacity-50
`;

// ---------------------------------------------------------------------------
// Skeleton row
// ---------------------------------------------------------------------------
function SkeletonRow() {
    return (
        <tr>
            {[1, 2, 3, 4, 5].map(i => (
                <td key={i} className="px-4 py-3">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" style={{ width: i === 3 ? '80px' : i === 4 ? '120px' : '60%' }} />
                </td>
            ))}
        </tr>
    );
}

// ---------------------------------------------------------------------------
// FrequencyCell — inline editing of a single row's frequency config
// ---------------------------------------------------------------------------
function FrequencyCell({ row, onSave, saving }) {
    const [frequency, setFrequency] = useState(row.frequency);
    const [dayOfWeek, setDayOfWeek] = useState(row.dayOfWeek ?? 1);
    const [dayOfMonth, setDayOfMonth] = useState(row.dayOfMonth ?? 1);

    const handleChange = async (newFreq, newDow, newDom) => {
        await onSave({
            locationId: row.locationId,
            orgId: row.orgId,
            frequency: newFreq,
            dayOfWeek: newFreq === 'weekly' ? newDow : null,
            dayOfMonth: newFreq === 'monthly' ? newDom : null,
            isEnabled: row.isEnabled,
        });
    };

    return (
        <div className="flex items-center gap-2 flex-wrap">
            <select
                value={frequency}
                onChange={e => { const v = e.target.value; setFrequency(v); handleChange(v, dayOfWeek, dayOfMonth); }}
                disabled={saving || !row.isEnabled}
                className={selectClass}
            >
                {Object.entries(FREQUENCY_LABELS).map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                ))}
            </select>

            {frequency === 'weekly' && (
                <select
                    value={dayOfWeek}
                    onChange={e => { const v = parseInt(e.target.value, 10); setDayOfWeek(v); handleChange(frequency, v, dayOfMonth); }}
                    disabled={saving || !row.isEnabled}
                    className={selectClass}
                >
                    {DAYS_OF_WEEK.map(d => (
                        <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                </select>
            )}

            {frequency === 'monthly' && (
                <div className="flex items-center gap-1">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Day</span>
                    <input
                        type="number"
                        min={1}
                        max={31}
                        value={dayOfMonth}
                        onChange={e => { const v = Math.min(31, Math.max(1, parseInt(e.target.value, 10) || 1)); setDayOfMonth(v); handleChange(frequency, dayOfWeek, v); }}
                        disabled={saving || !row.isEnabled}
                        className={`${selectClass} w-16 text-center`}
                    />
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// EnabledToggle — pill-style toggle switch
// ---------------------------------------------------------------------------
function EnabledToggle({ row, onSave, saving }) {
    return (
        <button
            onClick={() => onSave({ locationId: row.locationId, orgId: row.orgId, frequency: row.frequency, dayOfWeek: row.dayOfWeek, dayOfMonth: row.dayOfMonth, isEnabled: !row.isEnabled })}
            disabled={saving}
            title={row.isEnabled ? 'Disable' : 'Enable'}
            className="flex items-center gap-2 disabled:opacity-50"
        >
            <span className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${row.isEnabled ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
                <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${row.isEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
            </span>
            <span className={`text-sm ${row.isEnabled ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500'}`}>
                {row.isEnabled ? 'On' : 'Off'}
            </span>
        </button>
    );
}

// ---------------------------------------------------------------------------
// BulkActionBar — shown when rows are selected
// ---------------------------------------------------------------------------
function BulkActionBar({ selectedCount, onApply, onClear, applying }) {
    const [frequency, setFrequency] = useState('daily');
    const [dayOfWeek, setDayOfWeek] = useState(1);
    const [dayOfMonth, setDayOfMonth] = useState(1);

    return (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-gray-900 dark:bg-gray-700 text-white rounded-xl shadow-xl px-4 py-3">
            <span className="text-sm font-medium whitespace-nowrap">
                {selectedCount} selected
            </span>

            <div className="w-px h-5 bg-gray-600 dark:bg-gray-500" />

            <select
                value={frequency}
                onChange={e => setFrequency(e.target.value)}
                disabled={applying}
                className="text-sm rounded-md px-2 py-1 bg-gray-800 dark:bg-gray-600 text-white border border-gray-600 dark:border-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:opacity-50"
            >
                {Object.entries(FREQUENCY_LABELS).map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                ))}
            </select>

            {frequency === 'weekly' && (
                <select
                    value={dayOfWeek}
                    onChange={e => setDayOfWeek(parseInt(e.target.value, 10))}
                    disabled={applying}
                    className="text-sm rounded-md px-2 py-1 bg-gray-800 dark:bg-gray-600 text-white border border-gray-600 dark:border-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:opacity-50"
                >
                    {DAYS_OF_WEEK.map(d => (
                        <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                </select>
            )}

            {frequency === 'monthly' && (
                <div className="flex items-center gap-1">
                    <span className="text-sm text-gray-400">Day</span>
                    <input
                        type="number"
                        min={1}
                        max={31}
                        value={dayOfMonth}
                        onChange={e => setDayOfMonth(Math.min(31, Math.max(1, parseInt(e.target.value, 10) || 1)))}
                        disabled={applying}
                        className="text-sm rounded-md px-2 py-1 w-16 text-center bg-gray-800 dark:bg-gray-600 text-white border border-gray-600 dark:border-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:opacity-50"
                    />
                </div>
            )}

            <button
                onClick={() => onApply({ frequency, dayOfWeek, dayOfMonth })}
                disabled={applying}
                className="text-sm font-medium bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg px-3 py-1.5 transition-colors whitespace-nowrap"
            >
                {applying ? 'Applying…' : 'Apply'}
            </button>

            <button
                onClick={onClear}
                disabled={applying}
                title="Clear selection"
                className="text-gray-400 hover:text-white transition-colors disabled:opacity-50"
            >
                <X size={16} />
            </button>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function NightAuditAdminPage() {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [savingIds, setSavingIds] = useState(new Set());
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [applying, setApplying] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const data = await getNightAuditAdminConfigs();
                setLocations(data.locations || []);
            } catch (err) {
                console.error('Failed to load night audit configs:', err);
                toast.error('Failed to load configuration');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const handleSave = useCallback(async (payload) => {
        setSavingIds(prev => new Set(prev).add(payload.locationId));
        try {
            await updateNightAuditAdminConfig(payload);
            setLocations(prev => prev.map(loc =>
                loc.locationId === payload.locationId ? { ...loc, ...payload } : loc
            ));
        } catch (err) {
            console.error('Failed to save config:', err);
            toast.error('Failed to save');
        } finally {
            setSavingIds(prev => { const next = new Set(prev); next.delete(payload.locationId); return next; });
        }
    }, []);

    const filtered = locations.filter(loc => {
        if (!search) return true;
        const q = search.toLowerCase();
        return loc.locationName?.toLowerCase().includes(q) || loc.orgName?.toLowerCase().includes(q);
    });

    const allFilteredSelected = filtered.length > 0 && filtered.every(l => selectedIds.has(l.locationId));

    const toggleSelectAll = () => {
        if (allFilteredSelected) {
            setSelectedIds(prev => {
                const next = new Set(prev);
                filtered.forEach(l => next.delete(l.locationId));
                return next;
            });
        } else {
            setSelectedIds(prev => {
                const next = new Set(prev);
                filtered.forEach(l => next.add(l.locationId));
                return next;
            });
        }
    };

    const toggleRow = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleBulkApply = async ({ frequency, dayOfWeek, dayOfMonth }) => {
        setApplying(true);
        const targets = locations.filter(l => selectedIds.has(l.locationId));
        try {
            await Promise.all(targets.map(loc =>
                updateNightAuditAdminConfig({
                    locationId: loc.locationId,
                    orgId: loc.orgId,
                    frequency,
                    dayOfWeek: frequency === 'weekly' ? dayOfWeek : null,
                    dayOfMonth: frequency === 'monthly' ? dayOfMonth : null,
                    isEnabled: loc.isEnabled,
                })
            ));
            setLocations(prev => prev.map(loc =>
                selectedIds.has(loc.locationId)
                    ? { ...loc, frequency, dayOfWeek: frequency === 'weekly' ? dayOfWeek : null, dayOfMonth: frequency === 'monthly' ? dayOfMonth : null }
                    : loc
            ));
            setSelectedIds(new Set());
        } catch (err) {
            console.error('Bulk apply failed:', err);
            toast.error('Failed to apply to some locations');
        } finally {
            setApplying(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link to="/admin/users" className="text-gray-500 hover:text-orange-500 dark:text-gray-400 dark:hover:text-orange-400 transition-colors">
                            <ArrowLeft size={20} />
                        </Link>
                        <div className="flex items-center gap-2">
                            <CalendarClock size={22} className="text-orange-500" />
                            <h1 className="text-xl font-semibold text-gray-800 dark:text-white">Night Audit Analysis Schedule</h1>
                        </div>
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                        {locations.length} location{locations.length !== 1 ? 's' : ''}
                    </span>
                </div>
                <p className="mt-1 ml-9 text-sm text-gray-500 dark:text-gray-400">
                    Configure how often each property's night audit files are submitted for AI risk analysis.
                    Files arriving on non-scheduled days are skipped — regular ETL processing is unaffected.
                </p>
            </div>

            {/* Search */}
            <div className="px-6 py-4">
                <div className="relative max-w-sm">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search locations or orgs…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="px-6 pb-24">
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
                  <div className="overflow-auto max-h-[70vh]">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                                <th className="px-4 py-3 w-10">
                                    <input
                                        type="checkbox"
                                        checked={allFilteredSelected}
                                        onChange={toggleSelectAll}
                                        disabled={loading || filtered.length === 0}
                                        className="rounded border-gray-300 dark:border-gray-600 text-orange-500 focus:ring-orange-400"
                                    />
                                </th>
                                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Location</th>
                                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Organization</th>
                                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Status</th>
                                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Frequency</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                            {loading ? (
                                Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-400 dark:text-gray-500">
                                        {search ? 'No locations match your search.' : 'No locations found.'}
                                    </td>
                                </tr>
                            ) : (
                                filtered.map(loc => {
                                    const saving = savingIds.has(loc.locationId);
                                    const selected = selectedIds.has(loc.locationId);
                                    return (
                                        <tr
                                            key={loc.locationId}
                                            className={`transition-colors cursor-pointer ${
                                                selected
                                                    ? 'bg-orange-50 dark:bg-orange-900/10'
                                                    : loc.isEnabled
                                                        ? 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                                                        : 'bg-gray-50/60 dark:bg-gray-900/30 opacity-70'
                                            }`}
                                            onClick={() => toggleRow(loc.locationId)}
                                        >
                                            <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={selected}
                                                    onChange={() => toggleRow(loc.locationId)}
                                                    className="rounded border-gray-300 dark:border-gray-600 text-orange-500 focus:ring-orange-400"
                                                />
                                            </td>
                                            <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">
                                                {loc.locationName}
                                            </td>
                                            <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                                                {loc.orgName || '—'}
                                            </td>
                                            <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                                                <EnabledToggle row={loc} onSave={handleSave} saving={saving} />
                                            </td>
                                            <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                                                <FrequencyCell key={`${loc.locationId}-${loc.frequency}-${loc.dayOfWeek}-${loc.dayOfMonth}`} row={loc} onSave={handleSave} saving={saving} />
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                  </div>
                </div>
            </div>

            {/* Bulk action bar */}
            {selectedIds.size > 0 && (
                <BulkActionBar
                    selectedCount={selectedIds.size}
                    onApply={handleBulkApply}
                    onClear={() => setSelectedIds(new Set())}
                    applying={applying}
                />
            )}
        </div>
    );
}
